/**
 * EF Epic 0 E0.6 — Legacy MUI typography variant → Design System 2.0 ramp mapping.
 * Input to A3 theme consolidation. Do not invent new variants in feature work.
 *
 * New ramp (DESIGN.md): display, h1, h2, h3, body, body-sm, caption, micro, nano,
 * metric, numeric, numeric-sm, kbd.
 */

/** @typedef {{ mapsTo: string, element: string, notes: string }} VariantMap */

/** @type {Record<string, VariantMap>} */
const NEW_VARIANTS = {
  display: { mapsTo: 'display', element: 'h1', notes: 'Page-level brand/hero only' },
  h1: { mapsTo: 'h1', element: 'h1', notes: 'Primary page title' },
  h2: { mapsTo: 'h2', element: 'h2', notes: 'Section title' },
  h3: { mapsTo: 'h3', element: 'h3', notes: 'Subsection / card title' },
  body: { mapsTo: 'body', element: 'p', notes: 'Default prose' },
  'body-sm': { mapsTo: 'body-sm', element: 'p', notes: 'Dense secondary prose' },
  caption: { mapsTo: 'caption', element: 'span', notes: 'Supporting labels' },
  micro: { mapsTo: 'micro', element: 'span', notes: 'Pointer-only; uppercase tracking labels' },
  nano: { mapsTo: 'nano', element: 'span', notes: 'Pointer-only; densest chrome labels' },
  metric: { mapsTo: 'metric', element: 'span', notes: 'Large numeric hero values' },
  numeric: { mapsTo: 'numeric', element: 'span', notes: 'Tabular monospace figures' },
  'numeric-sm': { mapsTo: 'numeric-sm', element: 'span', notes: 'Dense tabular figures' },
  kbd: { mapsTo: 'kbd', element: 'kbd', notes: 'Keyboard hints' },
};

/**
 * Legacy MUI / theme.js variants still live in the tree (~1.9k sites).
 * A3 must declare these on the theme so they do not fall through fontSize: 15.
 *
 * @type {Record<string, VariantMap>}
 */
const LEGACY_VARIANT_MAPPING = {
  body1: { mapsTo: 'body', element: 'p', notes: 'Track B style migration → body; A3 preserves legacy visuals' },
  body2: { mapsTo: 'body-sm', element: 'p', notes: 'Track B style migration → body-sm; A3 preserves legacy visuals' },
  subtitle1: {
    mapsTo: 'h3',
    element: 'h6',
    notes: 'Semantic target h3 deferred; keep MUI default element until Track B audit (A3 D2)',
  },
  subtitle2: { mapsTo: 'body-sm', element: 'p', notes: 'Track B; A3 preserves legacy visuals' },
  h4: { mapsTo: 'h3', element: 'h4', notes: 'Track B collapse into h3 scale; keep h4 element' },
  h5: { mapsTo: 'h3', element: 'h5', notes: 'Track B collapse into h3 scale; keep h5 element' },
  h6: { mapsTo: 'body', element: 'h6', notes: 'Track B; keep h6 element' },
  button: { mapsTo: 'body-sm', element: 'span', notes: 'Track B via StackButton' },
  overline: { mapsTo: 'micro', element: 'span', notes: 'Track B alias → micro' },
  caption: { mapsTo: 'caption', element: 'span', notes: 'Already on ramp; keep' },
};

/** variantMapping for createTheme — semantic HTML element per new variant */
const VARIANT_MAPPING = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  body: 'p',
  'body-sm': 'p',
  caption: 'span',
  micro: 'span',
  nano: 'span',
  metric: 'span',
  numeric: 'span',
  'numeric-sm': 'span',
  kbd: 'kbd',
};

module.exports = {
  NEW_VARIANTS,
  LEGACY_VARIANT_MAPPING,
  VARIANT_MAPPING,
};
